import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { FamilyGroupGetInfoResp } from 'app/shared/models/family-group.model';
import { IVKUserInfo } from 'app/shared/models/vk.interfaces';
import { Observable, of, throwError, from } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { SP_ERROR_HANDLERS } from '../common/http-error-handlers';
import { ApiConnectionService } from '../common/utils/apiConnectionService';
import {
  joinBaseAndPath,
  requestWithFallback,
  requestWithFallbackSync,
  SyncRequestError,
} from '../common/utils/requestWithFallback';
import { ClubInfo, ICertificate } from '../shared/models/certificates.model';
import { City } from '../shared/models/city.model';
import { Class } from '../shared/models/class.model';
import { ClubDownTimeGetByClubReq } from '../shared/models/club-down-time.model';
import { Club } from '../shared/models/club.model';
import { Coach } from '../shared/models/coach.model';
import { ApiResponse } from '../shared/models/common.models';
import { Course } from '../shared/models/course.model';
import { SPErrorHandler } from '../shared/models/misc.models';
import { PagedList } from '../shared/models/paged-list.model';
import {
  isVkParams,
  isWParams,
  QueryStringParams,
} from '../shared/models/parameters.model';
import {
  BookingRes,
  ILoginData,
  IToken,
  IUserInfo,
} from '../shared/models/profile.model';
import { IRegisterData } from '../shared/models/register-data.model';
import { Room } from '../shared/models/room.model';
import {
  ConfirmVisitInfoRes,
  CreateClassReq,
  GetClassReq,
  GetSubscriptionsPlanReq,
  GetVirtualClassReq,
  GetWeekScheduleReq,
  IIndividualBookFreeReq,
  IRentBookFreeReq,
  IRequestConfirmVisit,
  IRequestRegularBookDetails,
  RawScheduleResponse,
  SaveUserInfoReq,
} from '../shared/models/schedule.model';
import { SubscriptionPlan } from '../shared/models/subscriptions.model';
import { WidgetSettings } from '../shared/models/widget.model';
import { IClubDictionaryData } from './../shared/models/club-dictionary-data.model';
import { SmartCaptchaService } from './smart-captcha/smart-captcha.service';
import { AppSettings, StorageService } from './storage.service';
import { IVKAccessTokeInfo, IVKAccessTokenRes } from './vk-auth.service';

@Injectable()
export class ApiService {
  private apiBaseUrl: string = environment.api;
  private siteBaseUrl: string = environment.siteBaseUrl;

  constructor(
    private http: HttpClient,
    private apiConnection: ApiConnectionService,
    private storageService: StorageService,
    private smartCaptcha: SmartCaptchaService,
    @Optional()
    @Inject(SP_ERROR_HANDLERS)
    private additionalErrorHandlers: SPErrorHandler[],
  ) {}

  public readonly cities = ((api: this) => ({
    get(id: string, useDefaultErrorHandling = true) {
      return api.post<City>('common-getCity', { id }, useDefaultErrorHandling);
    },
  }))(this);

  public readonly search = ((api: this) => {
    return {
      clubs(
        text: string,
        useDefaultErrorHandling = true,
      ): Observable<ApiResponse<PagedList<Club>>> {
        return api.post<PagedList<Club>>(
          'common-searchClubs',
          { text },
          useDefaultErrorHandling,
        );
      },
    };
  })(this);

  public readonly common = ((api: this) => {
    return {
      getBranches(clubId: string, useDefaultErrorHandling = true): any {
        return api.get(
          'get-branches',
          { clubId: clubId },
          useDefaultErrorHandling,
        );
      },
      getClubRefs(clubId: string) {
        return api.get<{
          roomToCourses: { id: string; ids: string[] }[];
          courseToCoaches: { id: string; ids: string[] }[];
          courseToSubsplans: { id: string; ids: string[] }[];
        }>('get-clubrefs', { clubId: clubId });
      },
      calculateSubscriptionCost(subVariantId: string) {
        return api.post('user-calculateSubsCost', { id: subVariantId });
      },
      getClientByClub(clubId: string, userId: string) {
        return api.post('client-getInfoByClub', {
          clubId: clubId,
          userId: userId,
        });
      },
    };
  })(this);

  public readonly clubConsents = ((api: this) => ({
    getPendingByClub(id: string, userId?: string) {
      const params: { id: string; userId?: string } = { id };
      if (userId) {
        params.userId = userId;
      }
      return api
        .get<
          import('../shared/models/club-consent.model').ClubConsentPendingItem[]
        >('clubConsents-getPendingByClub', params)
        .pipe(map((r: any) => r?.result ?? []));
    },
    getPendingByClubs(ids: string[], userId?: string) {
      const url = userId
        ? `clubConsents-getPendingByClubs?userId=${encodeURIComponent(userId)}`
        : 'clubConsents-getPendingByClubs';
      return api
        .apiPost<
          import('../shared/models/club-consent.model').ClubConsentPendingItem[]
        >(url, ids as any)
        .pipe(map((r: any) => r?.result ?? []));
    },
    accept(body: { clubId: string; versionIds: string[] }) {
      return api.apiPost('clubConsents-accept', body as any);
    },
  }))(this);

  public readonly settings = ((api: this) => ({
    getByWidgetId(req: QueryStringParams, useDefaultErrorHandling = true) {
      if (isVkParams(req)) {
        return api.post<WidgetSettings>(
          'common-getSettings',
          { groupid: req.group_id, appid: req.api_id },
          useDefaultErrorHandling,
        );
      } else if (isWParams(req)) {
        return api.post<WidgetSettings>(
          'common-getSettings',
          { id: req.w, clubId: req.clubId },
          useDefaultErrorHandling,
        );
      }
    },
  }))(this);

  public readonly clubSettings = ((api: this) => {
    return {
      getByClub(_id: string, useDefaultErrorHandling = true): any {
        return api.get(
          '../../wg/clubSettings-getByClub',
          { id: _id },
          useDefaultErrorHandling,
        );
      },
      checkAllowBookingsOnlyClubClients(
        clubId: string,
        userId: string,
        useDefaultErrorHandling = true,
      ): any {
        return api.get(
          '../../wg/clubSettings-CheckAllowBookingsOnlyClubClients',
          { clubId, userId },
          useDefaultErrorHandling,
        );
      },
    };
  })(this);

  public clubDictionaries = ((api: this) => {
    return {
      get(clubId: string, alias: string) {
        return api.apiPost<IClubDictionaryData>('../../v1/clubDictionary-get', {
          clubId: clubId,
          alias: alias,
        });
      },
      save(dic: IClubDictionaryData) {
        return api.apiPost<IClubDictionaryData>(
          '../../v1/clubDictionary-save',
          dic,
        );
      },
      remove(dic: IClubDictionaryData) {
        return api.apiPost<IClubDictionaryData>(
          '../../v1/clubDictionary-remove',
          dic,
        );
      },
      getByClub(clubId: string) {
        return api.apiPost<Array<IClubDictionaryData>>(
          `../../v1/clubDictionaryItems-getByClub`,
          { clubId },
        );
      },
      setIsDefault(dic: IClubDictionaryData) {
        return api.apiPost<Array<IClubDictionaryData>>(
          `../../v1/clubDictionary-setIsDefault`,
          dic,
        );
      },
      getIsDefault(clubId: string) {
        return api.apiPost<IClubDictionaryData>(
          `../../v1/clubDictionary-getIsDefault`,
          { clubId },
        );
      },
    };
  })(this);

  public readonly subscriptionPlans = ((api: this) => ({
    getByCourse(req: GetSubscriptionsPlanReq) {
      return api.apiPost<Array<SubscriptionPlan>>(
        'subscriptionPlans-getByCourse',
        req,
      );
    },
    getByClub(id: string, useDefaultErrorHandling = true, isCert = false) {
      return api.post<Array<SubscriptionPlan>>(
        'subscriptionPlans-getByClub',
        { id, isCert },
        useDefaultErrorHandling,
      );
    },
    get(id: string, useDefaultErrorHandling = true) {
      return api.post<SubscriptionPlan>(
        'subscriptionPlans-get',
        { id },
        useDefaultErrorHandling,
      );
    },
    clientSubscriptionLimit(
      userId: string,
      subscriptionPlanVariantId: string,
      useDefaultErrorHandling = true,
    ) {
      return api.post<any>(
        'clientSubscriptionLimit',
        {
          userId: userId,
          subscriptionPlanVariantId: subscriptionPlanVariantId,
        },
        useDefaultErrorHandling,
      );
    },
    clientSubscriptionGetByUser(clubId: string, userId: string) {
      return api.apiPost<Array<any>>('clientSubscription-getByUser', {
        clubId: clubId,
        userId: userId,
      });
    },
  }))(this);

  public readonly courses = ((api: this) => ({
    getByClub(id: string, useDefaultErrorHandling = true) {
      return api.post<Array<Course>>(
        'courses-getByClub',
        { id },
        useDefaultErrorHandling,
      );
    },
    get(id: string, useDefaultErrorHandling = true) {
      return api.post<Course>('courses-get', { id }, useDefaultErrorHandling);
    },
  }))(this);

  public readonly rooms = ((api: this) => ({
    getByClub(id: string, useDefaultErrorHandling = true) {
      return api.post<Array<Room>>(
        'rooms-getByClub',
        { id },
        useDefaultErrorHandling,
      );
    },
    get(id: string, useDefaultErrorHandling = true) {
      return api.post<Room>('rooms-get', { id }, useDefaultErrorHandling);
    },
  }))(this);

  public readonly coaches = ((api: this) => ({
    getByClub(id: string, useDefaultErrorHandling = true) {
      return api.post<Array<Coach>>(
        'coaches-getByClub',
        { id },
        useDefaultErrorHandling,
      );
    },
    get(id: string, useDefaultErrorHandling = true) {
      return api.post<Coach>('coaches-get', { id }, useDefaultErrorHandling);
    },
  }))(this);

  public readonly schedule = ((api: this) => ({
    createClassRequest(req: CreateClassReq, useDefaultErrorHandling = true) {
      return api.post(
        'schedule-createClassRequest',
        { model: req },
        useDefaultErrorHandling,
      );
    },

    getClubSchedule(req: GetWeekScheduleReq, useDefaultErrorHandling = true) {
      return api.post<RawScheduleResponse>(
        'schedule-getWeek',
        req,
        useDefaultErrorHandling,
      );
    },

    getNearFuture(req: GetWeekScheduleReq, useDefaultErrorHandling = true) {
      return api.post<RawScheduleResponse>(
        'schedule-nearFuture',
        req,
        useDefaultErrorHandling,
      );
    },

    getVirtualClass(req: GetVirtualClassReq, useDefaultErrorHandling = true) {
      return api.post<Class>(
        'schedule-getVirtualClass',
        req,
        useDefaultErrorHandling,
      );
    },

    getClass(req: GetClassReq) {
      return api.apiPost<Class>('schedule-getClass', req);
    },

    saveUserInfo(req: SaveUserInfoReq, useDefaultErrorHandling = true) {
      return api.apiPost<any>('schedule-saveUserInfo', req);
    },
  }))(this);

  public readonly clubDownTime = ((api: this) => ({
    getClubDownTimeByClubId(req: ClubDownTimeGetByClubReq) {
      return api.post('ClubDownTimeGetByClubId', req, true);
    },
  }))(this);

  public readonly slotHold = ((api: this) => ({
    acquire(req: any) {
      return api.apiPost<any>('slotHold-acquire', req);
    },
    release(req: any) {
      return api.apiPost<boolean>('slotHold-release', req);
    },
    getBusy(req: any) {
      return api.apiPost<any[]>('slotHold-getBusy', req);
    },
  }))(this);

  public readonly certificates = ((api: this) => ({
    getList(_type: number, _clubId: string, useDefaultErrorHandling = true) {
      return api.get(
        '../../v1/app/certificatesUmbraco-getAll',
        { type: _type, clubId: _clubId },
        useDefaultErrorHandling,
      );
    },
    generateNewCertificate(
      model: ICertificate,
      useDefaultErrorHandling = true,
    ) {
      return api.post<any>(
        '../../v1/app/certificate/generate',
        model,
        useDefaultErrorHandling,
      );
    },
    generateNewCertificateSync(
      model: ICertificate,
      useDefaultErrorHandling = true,
    ) {
      return api.postSync(
        '../../v1/app/certificate/generate',
        model,
        useDefaultErrorHandling,
      );
    },
    getInfoByIds(ids: string[], useDefaultErrorHandling = true) {
      return api.post<ClubInfo[]>(
        'club-getInfoByIds',
        ids,
        useDefaultErrorHandling,
      );
    },
  }))(this);

  public readonly club = ((api: this) => ({
    getInfoByIds(ids: string[], useDefaultErrorHandling = true) {
      return api.post<ClubInfo[]>(
        'club-getInfoByIds',
        ids,
        useDefaultErrorHandling,
      );
    },
  }))(this);

  private handleErrors = ((api: this) => (error: HttpErrorResponse) => {
    if (api.additionalErrorHandlers) {
      api.additionalErrorHandlers.forEach((handler) => handler.handle(error));
    }
    return this.mapHttpErrorToApiResponse(error);
  })(this);

  public readonly userAccounts = ((api: this) => ({
    login(loginData: ILoginData, useDefaultErrorHandling = true) {
      return api.apiPost<IToken>('account/login', loginData);
    },
    getProfile() {
      return api.apiPost<IUserInfo>('context-getUserInfo', {});
    },
    loginFirebase(accessToken: any) {
      return api.apiPost<IToken>('account/login-firebase', accessToken);
    },
    beginPhoneRegistration(phone: string, isCallCode: boolean) {
      return api.withCaptchaToken().pipe(
        switchMap((captchaToken) =>
          api.apiPost(`../../v1/account/begin-phone-registration`, {
            phone: phone,
            isCallCode: isCallCode,
            captchaToken,
          }),
        ),
      );
    },
    completePhoneRegistration(authToken: string, confirmationToken: string) {
      return api.apiPost<IToken>(
        `../../v1/account/complete-phone-registration`,
        { authToken, confirmationToken },
      );
    },
    completePushPhoneRegistration(authToken: string) {
      return api.apiPost<IToken>(
        `../../v1/account/complete-push-phone-registration`,
        { authToken },
      );
    },
    updateConsent(
      userId: string,
      consent: { personalDataConsent: boolean; offerAgreement: boolean },
    ) {
      return api.apiPost(`../../v1/accounts/${userId}/update-consent`, consent);
    },
    beginEmailRegistration(email: string) {
      return api.apiPost(`../../v1/account/begin-email-registration`, {
        email: email,
      });
    },
    completeEmailRegistration(authToken: string, confirmationToken: string) {
      return api.apiPost(`../../v1/account/complete-email-registration`, {
        authToken,
        confirmationToken,
      });
    },
    beginPushLogin(emailOrPhone: string) {
      return api.withCaptchaToken().pipe(
        switchMap((captchaToken) =>
          api.apiPost(`../../v1/account/begin-push-login`, {
            emailOrPhone,
            captchaToken,
          }),
        ),
      );
    },
    completePushLogin(authToken: string, confirmationToken: string) {
      return api.apiPost(`../../v1/account/complete-push-login`, {
        authToken,
        confirmationToken,
      });
    },
    getRefreshToken(id: string) {
      return api.apiPost(`account/get-refresh-token`, { userId: id });
    },
    refreshByRefreshToken(id: string) {
      return api.apiPost(`account/get-token-by-refresh`, { userId: id });
    },
    isDuplicateEmail(email: string) {
      return api.apiPost('../../v2/wg/account/is-duplicate-email', {
        email: email,
      });
    },
    isDuplicatePhone(phone: string) {
      return api.apiPost('../../v1/account/is-duplicate-phone', {
        phone: phone,
      });
    },
    register(registerData: IRegisterData) {
      return api.apiPost('../../v1/account/register', registerData);
    },
    registerEmailAndPhone(registerData: IRegisterData) {
      return api.apiPost(
        '../../v1/account/register-email-and-phone',
        registerData,
      );
    },
    beginResetPassword(
      emailOrPhone: string,
      isSendNotification: boolean = false,
    ) {
      return api.withCaptchaToken().pipe(
        switchMap((captchaToken) =>
          api.apiPost(`../../v1/account/begin-reset-password`, {
            emailOrPhone,
            isSendNotification,
            isSpApplication: true,
            captchaToken,
          }),
        ),
      );
    },
    confirmRecoveryPassword(authToken: string, confirmationToken: string) {
      return api.apiPost(`../../v1/account/confirm-recovery-password`, {
        authToken,
        confirmationToken,
      });
    },
    completeRecoveryPassword(
      authToken: string,
      confirmationToken: string,
      newPassword: string,
    ) {
      return api.apiPost(`../../v1/account/complete-recovery-password`, {
        authToken,
        confirmationToken,
        newPassword,
      });
    },
    loginVk(vkUserInfo: IVKUserInfo) {
      return api
        .post<IToken>('../../v1/app/account-vkontakte/login', vkUserInfo)
        .pipe(map((response) => response as unknown as IToken));
    },
    getVkAccessToken(vKAccessTokeInfo: IVKAccessTokeInfo) {
      return api
        .post<IToken>(
          '../../v1/app/account-vkontakte/get-access_token',
          vKAccessTokeInfo,
        )
        .pipe(map((response) => response as unknown as IVKAccessTokenRes));
    },
    checkPushNotificationStatus(authToken: string) {
      return api.get('../../v1/account/check-push-notification-status', {
        authToken,
      });
    },
    getRegistrationForm() {
      return api
        .get<{
          registrationForm?: string;
        }>('../../v1/account/registration-form', {}, false)
        .pipe(map((res: any) => res?.result ?? res));
    },
    verifyRegistrationCode(
      authToken: string,
      confirmationToken: string,
      type: 'email' | 'phone' | 'push',
    ) {
      return api
        .apiPost<{
          valid: boolean;
        }>('../../v1/account/verify-registration-code', {
          authToken,
          confirmationToken,
          type,
        })
        .pipe(
          map((res: any) => ({
            valid: res?.valid === true || res?.result?.valid === true,
          })),
        );
    },
  }))(this);

  public readonly userSchedule = ((api: this) => ({
    bookRegular(request: IRequestRegularBookDetails) {
      return api.apiPost<BookingRes>(`classvisits-bookregular`, request);
    },
    bookFreeRent(request: IRentBookFreeReq) {
      return api.apiPost<BookingRes>(`classvisits-bookfree-rent`, request);
    },
    bookFreeIndividual(request: IIndividualBookFreeReq) {
      return api.apiPost<BookingRes>(
        `classvisits-bookfree-individual`,
        request,
      );
    },
    checkingBooking(request: IRequestRegularBookDetails) {
      return api.apiPost<BookingRes>(`classvisits-checkingBooking`, request);
    },
    confirmVisit(request: IRequestConfirmVisit) {
      return api.apiPost<ConfirmVisitInfoRes>(
        `classvisits-confirmClientVisit-by-qrcode`,
        request,
      );
    },
  }))(this);

  public readonly familyGroup = ((api: this) => ({
    getInfo(clubId: string) {
      return api
        .apiPost<FamilyGroupGetInfoResp>(`familyGroup-getInfo`, { clubId })
        .pipe(map((response) => response as unknown as FamilyGroupGetInfoResp));
    },
  }))(this);

  public setCulture(culture: string): void {
    this.culture = culture;
  }

  private culture: string;

  /** Токен SmartCaptcha если feature flag включён; иначе null. */
  private withCaptchaToken(): Observable<string | null> {
    return from(this.smartCaptcha.resolveToken()).pipe(
      catchError((e) =>
        throwError(
          e instanceof Error ? e : new Error('Не удалось пройти проверку капчи'),
        ),
      ),
    );
  }

  private mapHttpErrorToApiResponse<T>(error: HttpErrorResponse) {
    const res: ApiResponse<any> = {
      confirmEmail: false,
      confirmPhone: false,
      desc:
        (error.error && error.error.desc) ||
        (error.status === 0
          ? 'Произошла сетевая ошибка. Проверьте, что подключение к интернету работает стабильно.'
          : error.message),
      result: null,
      error,
    };
    return of(res);
  }

  private post<T>(
    url: string,
    params: { [key: string]: any },
    useDefaultErrorHandling = true,
  ) {
    const request = requestWithFallback(
      url,
      (fullUrl) =>
        this.http.post<ApiResponse<T>>(fullUrl, params, {
          headers: this.makeRequestOptionArgs(),
        }),
      this.apiConnection.apiUrlFallbackView,
    );

    if (useDefaultErrorHandling) {
      return request.pipe(
        catchError((error: HttpErrorResponse) => {
          return this.handleErrors(error);
        }),
      );
    } else {
      return request.pipe(
        catchError((error: HttpErrorResponse) => {
          return this.mapHttpErrorToApiResponse(error);
        }),
      );
    }
  }

  private get<T>(
    url: string,
    params: { [key: string]: any },
    useDefaultErrorHandling = true,
    isReqBaseSite = false,
  ) {
    const request = requestWithFallback(
      url,
      (fullUrl) => this.http.get<ApiResponse<T>>(fullUrl, { params }),
      this.apiConnection.apiUrlFallbackView,
      {
        buildFullUrl: joinBaseAndPath,
      },
    );

    if (useDefaultErrorHandling) {
      return request.pipe(
        catchError((error: HttpErrorResponse) => {
          return this.handleErrors(error);
        }),
      );
    } else {
      return request.pipe(
        catchError((error: HttpErrorResponse) => {
          return this.mapHttpErrorToApiResponse(error);
        }),
      );
    }
  }

  private apiPost<T>(url: string, params: { [key: string]: any }) {
    return requestWithFallback(
      url,
      (fullUrl) =>
        this.http.post<ApiResponse<T>>(fullUrl, params, {
          headers: this.makeRequestOptionArgs(),
        }),
      this.apiConnection.apiUrlFallbackView,
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(error); // все остальные ошибки наружу
      }),
    );
  }

  private postSync(
    url: string,
    params: { [key: string]: any },
    useDefaultErrorHandling = true,
  ) {
    try {
      return requestWithFallbackSync(
        url,
        (fullUrl) => this.executeXhrPost(fullUrl, params),
        this.apiConnection.apiUrlFallbackView,
      );
    } catch (err) {
      if (useDefaultErrorHandling) {
        const status = err instanceof SyncRequestError ? err.status : 0;
        const res = new HttpErrorResponse({
          error: (err as Error).message,
          status,
          statusText: err instanceof SyncRequestError ? '' : '',
        });
        this.handleErrors(res);
      }
      throw err;
    }
  }

  private executeXhrPost(fullUrl: string, params: { [key: string]: any }) {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', fullUrl, false);
    xhr.setRequestHeader('Content-Type', 'application/json');
    try {
      const token =
        this.storageService.read(AppSettings.token) ??
        this.getCookie(AppSettings.token);
      if (token)
        xhr.setRequestHeader('Authorization', `Bearer ${token.accessToken}`);
      const refreshToken = this.storageService.read(AppSettings.refresh);
      if (refreshToken) xhr.setRequestHeader('X-Refresh-Token', refreshToken);
    } catch {}

    xhr.send(JSON.stringify(params));

    return {
      status: xhr.status,
      data:
        xhr.status === 200 ? JSON.parse(xhr.responseText) : xhr.responseText,
    };
  }

  getCookie(name) {
    var cookie = ' ' + document.cookie;
    var search = ' ' + name + '=';
    var setStr = null;
    var offset = 0;
    var end = 0;
    if (cookie.length > 0) {
      offset = cookie.indexOf(search);
      if (offset != -1) {
        offset += search.length;
        end = cookie.indexOf(';', offset);
        if (end == -1) {
          end = cookie.length;
        }
        setStr = unescape(cookie.substring(offset, end));
      }
    }
    if (setStr) return JSON.parse(setStr);
  }

  private makeUrl(url: string, isReqBaseSite = false) {
    return isReqBaseSite
      ? `${this.siteBaseUrl}${url}`
      : `${this.apiBaseUrl}/${url}`;
  }

  private makeRequestOptionArgs() {
    let httpHeaders = new HttpHeaders().set('Content-Type', 'application/json');
    if (this.culture)
      httpHeaders = httpHeaders.set('Accept-Language', this.culture);

    // Чтобы работало в инкогнито /gIGRZ033/
    try {
      let token = this.storageService.read(AppSettings.token);

      if (!token) token = this.getCookie(AppSettings.token);

      if (token)
        httpHeaders = httpHeaders.set(
          'Authorization',
          `Bearer ${token.accessToken}`,
        );

      const refreshToken = this.storageService.read(AppSettings.refresh);
      if (refreshToken) httpHeaders.set('X-Refresh-Token', refreshToken);
    } catch {}

    return httpHeaders;
  }
}

// https://vk.com/add_community_app?aid=6445829&callback_url=http://sportpriorityapi.ud4.ru/v2/wg/v2/wg/vk-bind-group/93803909
